import { COP_API_BASE_URL, getPerformanceOverviewUrl } from '../../../constants/service-endpoints'
import { get } from '../../../services/http.service'
import { getSilentToken } from '../../../services/tokenUtils'

export type KpiWidget = {
  indicator: string
  type: string
  data: { value: number }
}

export type Section = {
  indicator: string
  widgets: KpiWidget[]
}

export type HomepageCountResponse = {
  sections: Section[]
}

type HomepageCountEnvelope =
  | HomepageCountResponse
  | {
    data?: HomepageCountResponse
  }

let cachedData: HomepageCountResponse | null = null
let cachedPromise: Promise<HomepageCountResponse | null> | null = null

function getPerformanceOverviewRequestUrl(): Promise<string> {
  const url = `${COP_API_BASE_URL}${getPerformanceOverviewUrl()}`

  const existingToken = getSilentToken()
  if (existingToken) {
    return Promise.resolve(url)
  }

  return new Promise(resolve => {
    const handleTokenAcquired = () => {
      document.removeEventListener('cop:token-acquired', handleTokenAcquired)
      resolve(url)
    }

    document.addEventListener('cop:token-acquired', handleTokenAcquired, { once: true })
  })
}

function normalizeHomepageCountResponse(response: HomepageCountEnvelope): HomepageCountResponse {
  if (response && typeof response === 'object' && Array.isArray((response as HomepageCountResponse).sections)) {
    return response as HomepageCountResponse
  }

  const nestedData = (response as { data?: HomepageCountResponse })?.data
  if (nestedData && Array.isArray(nestedData.sections)) {
    return nestedData
  }

  return { sections: [] }
}

export async function fetchHomepageCounts(): Promise<HomepageCountResponse | null> {

  if (cachedData !== null) {
    return cachedData
  }

  if (cachedPromise) {
    return cachedPromise
  }

  const url = await getPerformanceOverviewRequestUrl()
  cachedPromise = get<HomepageCountEnvelope>(url)
    .then(response => {
      const normalizedResponse = normalizeHomepageCountResponse(response)
      cachedData = normalizedResponse
      return normalizedResponse
    })
    .catch(() => {
      return null
    })
    .finally(() => {
      cachedPromise = null
    })

  return cachedPromise
}